import { ExternalLink, Globe, Linkedin, Instagram, Youtube, Twitter, Facebook, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SocialPlatform } from "@/data/models/types";
import TikTokIcon from "@/components/icons/TikTokIcon";
import DailymotionIcon from "@/components/icons/DailymotionIcon";

interface SocialLinksProps {
  links?: {
    platform: SocialPlatform;
    url: string;
  }[];
  onEdit?: (platform: SocialPlatform) => void;
  onRemove?: (platform: SocialPlatform) => void;
  readOnly?: boolean;
  socialLinks?: Record<string, string> | null;
  displayStyle?: 'default' | 'icons';
}

// Map of platform names to their respective icon components
const socialIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  tiktok: TikTokIcon,
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  dailymotion: DailymotionIcon,
  website: Globe
};

// Map of platform colors for better visibility
const platformColors: Record<string, string> = {
  tiktok: 'bg-black hover:bg-gray-800',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
  youtube: 'bg-red-600 hover:bg-red-700',
  twitter: 'bg-blue-400 hover:bg-blue-500',
  linkedin: 'bg-blue-700 hover:bg-blue-800',
  facebook: 'bg-blue-600 hover:bg-blue-700',
  dailymotion: 'bg-blue-500 hover:bg-blue-600',
  website: 'bg-green-600 hover:bg-green-700'
};

export const SocialLinks = ({ 
  links, 
  onEdit, 
  onRemove, 
  readOnly = false, 
  socialLinks, 
  displayStyle = 'default' 
}: SocialLinksProps) => {
  // Debug output to help identify issues
  console.log("SocialLinks received:", { links, socialLinks, displayStyle });
  
  // Convert socialLinks object to links array if provided
  const processedLinks = links || (socialLinks ? 
    Object.entries(socialLinks)
      .filter(([_, url]) => url && url.trim()) // Filter out empty values
      .map(([platform, url]) => ({ 
        platform: platform as SocialPlatform, 
        url 
      })) : []);
  
  // More detailed debugging to see exactly what we're working with
  console.log("Processed links:", processedLinks);
  console.log("Number of links:", processedLinks.length);
  
  const getPlatformColor = (platform: SocialPlatform) => {
    switch (platform.toLowerCase()) {
      case 'tiktok': return 'bg-black text-white';
      case 'instagram': return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'youtube': return 'bg-red-600 text-white';
      case 'twitter': return 'bg-blue-400 text-white';
      case 'linkedin': return 'bg-blue-700 text-white';
      case 'facebook': return 'bg-blue-600 text-white';
      case 'dailymotion': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };
  
  if (displayStyle === 'icons' && processedLinks.length > 0) {
    return (
      <div className="flex items-center gap-3">
        {processedLinks.map(({ platform, url }, index) => {
          // Normalize platform name for lookup
          const platformKey = platform.toLowerCase();
          console.log(`Rendering icon for platform "${platformKey}" (${index + 1}/${processedLinks.length})`);
          
          // Get the correct icon component or fallback to Globe
          const IconComponent = socialIcons[platformKey] || Globe;
          const bgColorClass = platformColors[platformKey] || 'bg-gray-600 hover:bg-gray-700';
          
          return (
            <a 
              key={`${platformKey}-${index}`}
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className={`text-white transition-colors p-2 rounded-full ${bgColorClass} shadow-sm hover:shadow`}
              aria-label={`Visit ${platform}`}
              title={platform}
            >
              <IconComponent className="h-5 w-5" />
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3">
      {processedLinks.map(({ platform, url }, index) => (
        <div key={`${platform}-${index}`} className="flex items-center gap-2">
          <Badge variant="outline" className={`px-3 py-1 ${getPlatformColor(platform.toLowerCase())}`}>
            {platform}
          </Badge>
          
          <a 
            href={url && typeof url === 'string' ? url.trim() : ''}
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-primary underline underline-offset-2 flex items-center gap-1"
          >
            {url && typeof url === 'string' ? url.trim() : ''}
            <ExternalLink size={12} />
          </a>
          
          {!readOnly && (
            <div className="flex items-center gap-1">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => onEdit(platform)}
                >
                  <Edit size={14} />
                </Button>
              )}
              
              {onRemove && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:text-destructive-foreground hover:bg-destructive"
                  onClick={() => onRemove(platform)}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          )}
        </div>
      ))}
      
      {processedLinks.length === 0 && (
        <p className="text-sm text-muted-foreground">Aucun lien social ajouté</p>
      )}
    </div>
  );
};
